import { getCurrentUserId } from "@risalabs_frontend_org/auth-wrapper";
import { type AuthLetterReviewDumpPayload } from "../../../api/postCall/authLetterReviewDump";
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import {
  BasicInfoFieldState,
  PLACEHOLDER,
  type ApprovalStatus,
  type ProcedureFieldState,
} from "./constants";
import {
  getAuthLetterPathFromJCode,
  getJCodeId,
  getProcedureFields,
  resolveNumVisitsForDump,
  toStorageDate,
} from "./saveReviewHelpers";

function statusToValidatedFlag(status?: ApprovalStatus): boolean | null {
  if (status === "valid") return true;
  if (status === "invalid") return false;
  return null;
}

/** Prefer current UI values over merged J-code fields for the dump API. */
function procedureFieldsForDump(
  j: JCodeDetails,
  procedureState?: ProcedureFieldState | null,
) {
  const base = getProcedureFields(j);
  const existingNumVisits = j.auth_letter?.calculated_visits;
  const numVisitsFormValue =
    procedureState?.numVisits ??
    (base.num_visits === "" ? PLACEHOLDER : base.num_visits);
  const num_visits = resolveNumVisitsForDump(
    numVisitsFormValue,
    existingNumVisits,
  );
  if (!procedureState) {
    return { ...base, num_visits };
  }
  return {
    ...base,
    j_code:
      procedureState.cptCode !== PLACEHOLDER
        ? procedureState.cptCode
        : base.j_code,
    auth_status:
      procedureState.authStatus !== PLACEHOLDER
        ? procedureState.authStatus
        : base.auth_status,
    start_date:
      procedureState.startDate !== PLACEHOLDER
        ? toStorageDate(procedureState.startDate)
        : base.start_date,
    end_date:
      procedureState.endDate !== PLACEHOLDER
        ? toStorageDate(procedureState.endDate)
        : base.end_date,
    dosage:
      procedureState.dosage !== PLACEHOLDER
        ? procedureState.dosage
        : base.dosage,
    num_visits,
    units_approved:
      procedureState.unitsApproved !== PLACEHOLDER
        ? procedureState.unitsApproved
        : base.units_approved,
  };
}

/**
 * Builds dump payload from explicit order/org ids and merged J-codes using tri-state review flags.
 *
 * @param orderId - PA order identifier
 * @param patientId - Patient identifier (MRN) required by auth letter submit API
 * @param orgId - Organization id for analytics/ML dump (aligned with Postgres RLS)
 * @param primaryPayerNameFallback - Last-resort payer label when UI and `auth_letter.primary_insurance` are empty
 */
export function buildAuthLetterReviewDumpPayload(
  orderId: string,
  patientId: string,
  orgId: string,
  primaryPayerNameFallback: string,
  mergedJCodes: JCodeDetails[],
  basicInfoFieldState?: BasicInfoFieldState | null,
  basicInfoStatus?: ApprovalStatus,
  diagnosisDetailStatus?: ApprovalStatus,
  procedureStatuses?: Record<string, ApprovalStatus>,
  procedureFieldStateMap?: Record<string, ProcedureFieldState> | null,
): AuthLetterReviewDumpPayload {
  const first = mergedJCodes[0];
  const authLetter = first?.auth_letter;
  const authLetterPath = first ? getAuthLetterPathFromJCode(first) : "";
  const bio = basicInfoFieldState;
  const primaryInsurance =
    bio != null && bio.primaryInsurance !== PLACEHOLDER
      ? bio.primaryInsurance
      : (authLetter?.primary_insurance ?? primaryPayerNameFallback);
  const memberId =
    bio != null && bio.memberId !== PLACEHOLDER
      ? (bio.memberId ?? "")
      : (authLetter?.member_id ?? "");
  const provider =
    bio != null && bio.providerName !== PLACEHOLDER
      ? (bio.providerName ?? "")
      : (authLetter?.provider ?? "");
  const authNumber =
    bio != null && bio.authorizationId !== PLACEHOLDER
      ? (bio.authorizationId ?? "")
      : (authLetter?.auth_letter_auth_number ?? "");
  const authInitiatedDate =
    bio != null && bio.authInitiatedDate !== PLACEHOLDER
      ? toStorageDate(bio.authInitiatedDate ?? "")
      : undefined;
  const sourcePortal =
    bio != null && bio.sourcePortal !== PLACEHOLDER
      ? (bio.sourcePortal ?? "")
      : (authLetter?.source_portal ?? undefined);
  const basicInfoValidated = statusToValidatedFlag(basicInfoStatus);
  const diagnosisValidated = statusToValidatedFlag(diagnosisDetailStatus);

  return {
    order_id: orderId,
    patient_id: patientId,
    org_id: orgId,
    user_id: getCurrentUserId() ?? "",
    payer_name: primaryInsurance,
    basic_info_validated: basicInfoValidated,
    diagnosis_validated: diagnosisValidated,
    auth_letter_path: authLetterPath,
    member_id: memberId,
    provider,
    auth_number: authNumber,
    ...(authInitiatedDate !== undefined && authInitiatedDate !== ""
      ? { auth_initiated_date: authInitiatedDate }
      : {}),
    ...(sourcePortal !== undefined && sourcePortal !== ""
      ? { source_portal: sourcePortal }
      : {}),
    j_code_entries: mergedJCodes.map((j) => {
      const id = getJCodeId(j);
      const fields = procedureFieldsForDump(
        j,
        procedureFieldStateMap?.[id] ?? null,
      );
      const human_validated = statusToValidatedFlag(procedureStatuses?.[id]);
      return {
        j_code: fields.j_code,
        auth_status: fields.auth_status,
        auth_validation_status: j.auth_letter?.auth_validation_status ?? "",
        start_date: fields.start_date,
        end_date: fields.end_date,
        num_visits: fields.num_visits,
        dosage: fields.dosage,
        drug_name: j.description ?? null,
        generic_name: j.medicine_name?.[1] ?? null,
        units_approved:
          fields.units_approved !== "" ? fields.units_approved : null,
        human_validated,
        basic_info_validated: basicInfoValidated,
        diagnosis_validated: diagnosisValidated,
      };
    }),
  };
}
